import type { RouteLocationRaw } from 'vue-router';
import type { AvailableReportsQuery } from '@/types/operations';
import { routeNames } from '@/constants/route-details.constant';
import { AppPermissions } from '@/utils/permissions';

/**
 * The Reports Center catalog (#645): the reports the server runs (fetched with
 * availableReports, already limited to what the user may run) next to the
 * report pages that existed before it, so staff find every report in one
 * place.
 */

export type ReportCategory = 'Students' | 'Academic' | 'Lecturers' | 'Attendance' | 'Billing';

export const REPORT_CATEGORIES: ReadonlyArray<{ key: ReportCategory; label: string; icon: string; blurb: string }> = [
  { key: 'Students', label: 'Students', icon: 'pi-users', blurb: 'Lists of students by year, campus, programme, class or course.' },
  { key: 'Academic', label: 'Academic', icon: 'pi-book', blurb: 'Report cards, course registration and results.' },
  { key: 'Lecturers', label: 'Lecturers', icon: 'pi-id-card', blurb: 'Who teaches what, and the students on each course.' },
  { key: 'Attendance', label: 'Attendance', icon: 'pi-calendar', blurb: 'Registers by day and attendance across the semester.' },
  { key: 'Billing', label: 'Billing', icon: 'pi-wallet', blurb: 'Payments, debtors and collections.' },
];

// @allow-handwritten a Reports Center card: server reports and older report pages merged, with a route and a New flag
export interface ReportCatalogEntry {
  key: string;
  title: string;
  description: string;
  category: ReportCategory;
  /** Export formats: xlsx, pdf, doc (or csv for some older pages). */
  formats: string[];
  to: RouteLocationRaw;
  /** Added with the Reports Center; flagged "New" on the page. */
  isNew: boolean;
}

export type EngineReport = AvailableReportsQuery['availableReports'][number];

interface PageReport extends Omit<ReportCatalogEntry, 'isNew'> {
  /** The route's own permission gate, so the card never leads to a 403. */
  permission?: { action: string; resource: string };
}

/** Report pages that predate the Reports Center. */
export const EXISTING_REPORT_PAGES: readonly PageReport[] = [
  {
    key: 'page:student-list',
    title: 'Student List Report',
    description: 'Students by calendar, faculty, department, programme, level, gender, status, nationality or custom field, with extra columns.',
    category: 'Students',
    formats: ['xlsx', 'pdf'],
    to: { name: 'admin-student-reports' },
    permission: { action: AppPermissions.Students.Actions.View, resource: AppPermissions.Students.Resource },
  },
  {
    key: 'page:broadsheet',
    title: 'Broadsheet',
    description: 'Every student\'s scores for a class or programme on one sheet.',
    category: 'Academic',
    formats: ['xlsx', 'pdf'],
    to: { name: 'admin-broadsheet-reports' },
    permission: { action: AppPermissions.Results.Actions.ViewBroadsheet, resource: AppPermissions.Results.Resource },
  },
  {
    key: 'page:daily-attendance',
    title: 'Daily Attendance',
    description: 'Take and review the register for a class or course on a given day.',
    category: 'Attendance',
    formats: [],
    to: { name: 'admin-daily-attendance' },
    permission: { action: AppPermissions.Attendance.Actions.View, resource: AppPermissions.Attendance.Resource },
  },
  {
    key: 'page:billing-reports',
    title: 'Billing Reports',
    description: 'Revenue, outstanding balances, payment methods, fee categories, relief, refunds and daily collections.',
    category: 'Billing',
    formats: ['xlsx', 'pdf'],
    to: { name: routeNames.ADMIN_BILLING_REPORTS.name },
    permission: { action: AppPermissions.Billing.Actions.ViewReports, resource: AppPermissions.Billing.Resource },
  },
  {
    key: 'page:debtors',
    title: 'Debtors',
    description: 'Students who still owe, with outstanding balances and guardian contacts.',
    category: 'Billing',
    formats: ['xlsx', 'pdf'],
    to: { name: routeNames.ADMIN_BILLING_DEBTORS.name },
    permission: { action: AppPermissions.Billing.Actions.ViewBillDetails, resource: AppPermissions.Billing.Resource },
  },
];

export const RUNNER_ROUTE_NAME = 'admin-report-runner';

export function isReportCategory(value: string): value is ReportCategory {
  return REPORT_CATEGORIES.some((category) => category.key === value);
}

/** Merge the server's reports with the pages this user can open, grouped by category in a fixed order. */
export function buildReportCatalog(
  engineReports: readonly EngineReport[],
  canOpen: (permission: { action: string; resource: string }) => boolean,
): Array<{ key: ReportCategory; label: string; icon: string; blurb: string; reports: ReportCatalogEntry[] }> {
  const fromEngine: ReportCatalogEntry[] = engineReports
    .filter((report) => isReportCategory(report.category))
    .map((report) => ({
      key: report.key,
      title: report.title,
      description: report.description,
      category: report.category as ReportCategory,
      formats: [...report.formats],
      to: { name: RUNNER_ROUTE_NAME, params: { key: report.key } },
      isNew: true,
    }));

  const fromPages: ReportCatalogEntry[] = EXISTING_REPORT_PAGES
    .filter((page) => !page.permission || canOpen(page.permission))
    .map(({ permission: _permission, ...page }) => ({ ...page, isNew: false }));

  return REPORT_CATEGORIES
    .map((category) => ({
      ...category,
      reports: [...fromEngine, ...fromPages].filter((report) => report.category === category.key),
    }))
    .filter((category) => category.reports.length > 0);
}

/** Case-insensitive match on title and description. */
export function matchesReportSearch(report: Pick<ReportCatalogEntry, 'title' | 'description'>, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return `${report.title} ${report.description}`.toLowerCase().includes(term);
}

const FORMAT_LABELS: Record<string, string> = { xlsx: 'Excel', pdf: 'PDF', doc: 'Word', csv: 'CSV' };

export function reportFormatLabel(format: string): string {
  return FORMAT_LABELS[format] ?? format.toUpperCase();
}

const FORMAT_ICONS: Record<string, string> = { xlsx: 'pi-file-excel', pdf: 'pi-file-pdf', doc: 'pi-file-word', csv: 'pi-file' };

export function reportFormatIcon(format: string): string {
  return FORMAT_ICONS[format] ?? 'pi-file';
}
